import { useEffect, useState } from "react";
import DashboardShell from "../components/DashboardShell";
import { api } from "../lib/api";
import { Bell, MessageSquare, Megaphone, Ticket } from "lucide-react";

const SECTIONS = [
  { id: "social", title: "Social", icon: Bell, color: "#39ff14", keys: [
    ["follows", "New follower"],
    ["reactions", "Reactions on your posts"],
    ["comments", "Comments on your posts"],
    ["mentions", "@mentions"],
    ["shares", "Shares of your posts"],
    ["badges", "Badges awarded"],
  ]},
  { id: "messages", title: "Messages", icon: MessageSquare, color: "#00aaff", keys: [
    ["new_message", "New message received"],
    ["read_receipts", "Read receipts"],
  ]},
  { id: "tickets", title: "Tickets", icon: Ticket, color: "#39ff14", keys: [
    ["created", "Ticket created"],
    ["assigned", "Ticket assigned"],
    ["updated", "Ticket updated"],
    ["resolved", "Ticket resolved"],
    ["closed", "Ticket closed"],
  ]},
  { id: "platform", title: "Platform", icon: Megaphone, color: "#00aaff", keys: [
    ["newsletters", "Newsletter"],
    ["announcements", "Announcements"],
  ]},
];

export default function NotificationSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/notifications/settings`).then((r) => setSettings(r.data.settings));
  }, []);

  const toggle = async (cat, key) => {
    if (!settings) return;
    setSaving(true);
    const next = {
      ...settings,
      [cat]: { ...settings[cat], [key]: !settings[cat][key] },
    };
    setSettings(next);
    try {
      await api.patch(`/notifications/settings`, { settings: next });
    } finally { setSaving(false); }
  };

  return (
    <DashboardShell>
      <div className="max-w-3xl mx-auto space-y-5" data-testid="notification-settings-page">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">// Settings · Notifications</div>
          <h1 className="text-2xl lg:text-3xl font-semibold mt-1">Notification preferences</h1>
          <p className="text-[#94a3b8] text-sm mt-1">Choose which events ping you in your bell, by category.</p>
        </div>

        {!settings ? (
          <div className="csc-card p-8 font-mono-tech text-xs text-[#94a3b8] uppercase tracking-wider">Loading…</div>
        ) : (
          SECTIONS.map(({ id, title, icon: Icon, color, keys }) => (
            <section key={id} className="csc-card" data-testid={`notification-section-${id}`}>
              <div className="flex items-center gap-2 px-5 py-3 border-b border-[rgba(0,170,255,0.18)]">
                <Icon size={16} style={{ color }} />
                <h2 className="font-semibold">{title}</h2>
              </div>
              <ul>
                {keys.map(([k, label]) => (
                  <li key={k} className="flex items-center justify-between px-5 py-3 border-b border-[rgba(0,170,255,0.18)] last:border-none">
                    <span className="text-sm">{label}</span>
                    <button
                      onClick={() => toggle(id, k)}
                      disabled={saving}
                      role="switch"
                      aria-checked={!!settings[id]?.[k]}
                      className={`w-12 h-6 rounded-full transition-all border ${settings[id]?.[k] ? "border-[#39ff14]" : "border-[rgba(0,170,255,0.18)]"}`}
                      style={settings[id]?.[k] ? { background: "rgba(57, 255, 20,0.25)", boxShadow: "0 0 14px rgba(57, 255, 20,0.35)" } : { background: "#050d1a" }}
                      data-testid={`toggle-${id}-${k}`}
                    >
                      <span
                        className="block w-4 h-4 rounded-full bg-white transition-transform"
                        style={{ transform: settings[id]?.[k] ? "translateX(26px)" : "translateX(4px)" }}
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </DashboardShell>
  );
}
